import { useState, useMemo, useEffect } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  LinearProgress,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { dicionarioDadosApi } from '../api/dicionariodados'
import ExportButton from '../components/ExportButton'
import AddButton from '../components/AddButton'
import ActionBar from '../components/ActionBar'
import type {
  DicionarioDadosRecord,
  DicionarioDadosCreatePayload,
  DicionarioDadosUpdatePayload,
  DicionarioDadosSortField,
} from '../api/dicionariodados'

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

const useDebouncedValue = <T,>(value: T, delay = 400): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

const DicionarioDadosPage: React.FC = () => {
  // Memoizar colunas do ExportButton para evitar re-renders
  const exportColumns = useMemo(() => [
    { key: 'banco', label: 'Banco' },
    { key: 'tabela', label: 'Tabela' },
    { key: 'coluna', label: 'Coluna' },
    { key: 'label_formulario', label: 'Label Formulário' },
    { key: 'label_grid', label: 'Label Grid' },
    { key: 'tipo_dado', label: 'Tipo Dado' },
    { key: 'obrigatorio', label: 'Obrigatório', format: (v: any) => v ? 'Sim' : 'Não' },
    { key: 'ativo', label: 'Ativo', format: (v: any) => v ? 'Sim' : 'Não' },
  ], [])
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [bancoFilter, setBancoFilter] = useState('')
  const [tabelaFilter, setTabelaFilter] = useState('')
  const [colunaFilter, setColunaFilter] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [sortField, setSortField] = useState<DicionarioDadosSortField>('id')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [formOpen, setFormOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<DicionarioDadosRecord | null>(null)
  const [formObrigatorio, setFormObrigatorio] = useState(false)
  const [formAtivo, setFormAtivo] = useState(true)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const debouncedSearch = useDebouncedValue(search)
  const debouncedBanco = useDebouncedValue(bancoFilter)
  const debouncedTabela = useDebouncedValue(tabelaFilter)
  const debouncedColuna = useDebouncedValue(colunaFilter)

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery<DicionarioDadosRecord[]>({
    queryKey: [
      'dicionario-dados',
      {
        search: debouncedSearch,
        banco: debouncedBanco,
        tabela: debouncedTabela,
        coluna: debouncedColuna,
        sortField,
        sortDirection,
      },
    ],
    queryFn: () =>
      dicionarioDadosApi.list({
        limit: 2000,
        texto: debouncedSearch || undefined,
        banco: debouncedBanco || undefined,
        tabela: debouncedTabela || undefined,
        coluna: debouncedColuna || undefined,
        sort_by: sortField,
        sort_direction: sortDirection,
      }),
    staleTime: 60_000,
  })

  const createMutation = useMutation({
    mutationFn: (payload: DicionarioDadosCreatePayload) => dicionarioDadosApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dicionario-dados'] })
      setFormOpen(false)
      setEditingRecord(null)
      setSnackbar({ open: true, message: 'Registro criado com sucesso!', severity: 'success' })
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error?.response?.data?.detail || 'Erro ao criar registro', severity: 'error' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: DicionarioDadosUpdatePayload }) =>
      dicionarioDadosApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dicionario-dados'] })
      setFormOpen(false)
      setEditingRecord(null)
      setSnackbar({ open: true, message: 'Registro atualizado com sucesso!', severity: 'success' })
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error?.response?.data?.detail || 'Erro ao atualizar registro', severity: 'error' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => dicionarioDadosApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dicionario-dados'] })
      setSnackbar({ open: true, message: 'Registro deletado com sucesso!', severity: 'success' })
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error?.response?.data?.detail || 'Erro ao deletar registro', severity: 'error' })
    },
  })

  const records = data ?? []

  const bancoOptions = useMemo(() => {
    const unique = new Set<string>()
    records.forEach((item) => {
      if (item.banco) {
        unique.add(item.banco)
      }
    })
    return Array.from(unique).sort((a, b) => a.localeCompare(b))
  }, [records])

  const tabelaOptions = useMemo(() => {
    const unique = new Set<string>()
    records
      .filter((item) => {
        if (!debouncedBanco) return true
        return item.banco?.toLowerCase().includes(debouncedBanco.trim().toLowerCase())
      })
      .forEach((item) => {
        if (item.tabela) {
          unique.add(item.tabela)
        }
      })
    return Array.from(unique).sort((a, b) => a.localeCompare(b))
  }, [records, debouncedBanco])

  const colunaOptions = useMemo(() => {
    const unique = new Set<string>()
    records
      .filter((item) => {
        if (debouncedBanco && !item.banco?.toLowerCase().includes(debouncedBanco.trim().toLowerCase())) return false
        if (debouncedTabela && !item.tabela?.toLowerCase().includes(debouncedTabela.trim().toLowerCase())) return false
        return true
      })
      .forEach((item) => {
        if (item.coluna) {
          unique.add(item.coluna)
        }
      })
    return Array.from(unique).sort((a, b) => a.localeCompare(b))
  }, [records, debouncedBanco, debouncedTabela])

  const filteredData = useMemo((): DicionarioDadosRecord[] => {
    if (!debouncedSearch) return records
    const term = debouncedSearch.trim().toLowerCase()

    return records.filter((item) => {
      const values = [
        item.banco,
        item.tabela,
        item.coluna,
        item.label_formulario,
        item.label_grid,
        item.descritivo_help,
        item.prompt_ia,
        item.tipo_dado,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return values.includes(term)
    })
  }, [records, debouncedSearch])

  const paginated = useMemo((): DicionarioDadosRecord[] => {
    const start = page * rowsPerPage
    return filteredData.slice(start, start + rowsPerPage)
  }, [filteredData, page, rowsPerPage])

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSort = (field: DicionarioDadosSortField) => {
    if (sortField !== field) {
      setSortField(field)
      setSortDirection('asc')
      setPage(0)
      return
    }
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    setPage(0)
  }

  const handleOpenForm = (record?: DicionarioDadosRecord) => {
    setEditingRecord(record || null)
    setFormObrigatorio(record?.obrigatorio || false)
    setFormAtivo(record?.ativo !== false)
    setFormOpen(true)
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setEditingRecord(null)
    setFormObrigatorio(false)
    setFormAtivo(true)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const payload: DicionarioDadosCreatePayload | DicionarioDadosUpdatePayload = {
      banco: formData.get('banco') as string,
      tabela: formData.get('tabela') as string,
      coluna: formData.get('coluna') as string,
      label_formulario: formData.get('label_formulario') as string || null,
      label_grid: formData.get('label_grid') as string || null,
      descritivo_help: formData.get('descritivo_help') as string || null,
      prompt_ia: formData.get('prompt_ia') as string || null,
      tipo_dado: formData.get('tipo_dado') as string || null,
      obrigatorio: formObrigatorio,
      tamanho_maximo: formData.get('tamanho_maximo') ? parseInt(formData.get('tamanho_maximo') as string, 10) : null,
      valores_possiveis: formData.get('valores_possiveis') as string || null,
      ativo: formAtivo,
    }

    if (editingRecord) {
      updateMutation.mutate({ id: editingRecord.id, payload })
    } else {
      createMutation.mutate(payload as DicionarioDadosCreatePayload)
    }
  }

  const handleDelete = (id: number) => {
    if (window.confirm('Tem certeza que deseja deletar este registro?')) {
      deleteMutation.mutate(id)
    }
  }

  const columns: Array<{
    key: DicionarioDadosSortField
    label: string
    align?: 'left' | 'right' | 'center'
  }> = [
    { key: 'banco', label: 'Banco' },
    { key: 'tabela', label: 'Tabela' },
    { key: 'coluna', label: 'Coluna' },
    { key: 'label_formulario', label: 'Label Formulário' },
    { key: 'label_grid', label: 'Label Grid' },
    { key: 'tipo_dado', label: 'Tipo' },
    { key: 'obrigatorio', label: 'Obrigatório', align: 'center' },
    { key: 'ativo', label: 'Ativo', align: 'center' },
  ]

  if (error) {
    return (
      <Box sx={{ width: '100%', py: 4, px: { xs: 2, md: 3 } }}>
        <Alert severity="error">
          {t('config.dicionarioDados.error', 'Erro ao carregar dados do dicionário.')}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', py: 4, px: { xs: 2, md: 3 } }}>
      <Box display="flex" flexDirection="column" gap={3}>
        <ActionBar>
          <ExportButton
            data={filteredData}
            columns={exportColumns}
            filename="dicionario_dados.xlsx"
            sheetName="Dicionário de Dados"
          />
          <AddButton onClick={() => handleOpenForm()} title="Novo Registro" />
        </ActionBar>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }}
          gap={2}
        >
          <Autocomplete
            freeSolo
            options={bancoOptions}
            value={bancoFilter}
            onChange={(_, newValue) => {
              setBancoFilter(newValue ?? '')
              setPage(0)
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setBancoFilter(newInputValue)
                setPage(0)
              }
              if (reason === 'clear') {
                setBancoFilter('')
                setPage(0)
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Banco"
                placeholder="Filtrar por banco..."
                fullWidth
              />
            )}
            noOptionsText="Nenhum resultado"
          />
          <Autocomplete
            freeSolo
            options={tabelaOptions}
            value={tabelaFilter}
            onChange={(_, newValue) => {
              setTabelaFilter(newValue ?? '')
              setPage(0)
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setTabelaFilter(newInputValue)
                setPage(0)
              }
              if (reason === 'clear') {
                setTabelaFilter('')
                setPage(0)
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tabela"
                placeholder="Filtrar por tabela..."
                fullWidth
              />
            )}
            noOptionsText="Nenhum resultado"
          />
          <Autocomplete
            freeSolo
            options={colunaOptions}
            value={colunaFilter}
            onChange={(_, newValue) => {
              setColunaFilter(newValue ?? '')
              setPage(0)
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setColunaFilter(newInputValue)
                setPage(0)
              }
              if (reason === 'clear') {
                setColunaFilter('')
                setPage(0)
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Coluna"
                placeholder="Filtrar por coluna..."
                fullWidth
              />
            )}
            noOptionsText="Nenhum resultado"
          />
          <TextField
            label="Buscar"
            placeholder="Buscar por texto..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(0)
            }}
            fullWidth
          />
        </Box>

        <Paper>
          {(isLoading || isFetching) && <LinearProgress />}
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => {
                    const isActive = sortField === column.key
                    return (
                      <TableCell
                        key={column.key}
                        align={column.align ?? 'left'}
                        sortDirection={isActive ? sortDirection : false}
                      >
                        <TableSortLabel
                          active={isActive}
                          direction={isActive ? sortDirection : 'asc'}
                          onClick={() => handleSort(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    )
                  })}
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      <Typography variant="body2">Carregando...</Typography>
                    </TableCell>
                  </TableRow>
                ) : paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum registro encontrado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.banco}</TableCell>
                      <TableCell>{item.tabela}</TableCell>
                      <TableCell>{item.coluna}</TableCell>
                      <TableCell>{item.label_formulario || '-'}</TableCell>
                      <TableCell>{item.label_grid || '-'}</TableCell>
                      <TableCell>{item.tipo_dado || '-'}</TableCell>
                      <TableCell align="center">{item.obrigatorio ? 'Sim' : 'Não'}</TableCell>
                      <TableCell align="center">{item.ativo ? 'Sim' : 'Não'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenForm(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(item.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            count={filteredData.length}
            labelRowsPerPage="Linhas por página:"
          />
        </Paper>
      </Box>

      {/* Dialog de formulário */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingRecord ? 'Editar Registro' : 'Novo Registro'}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 2 }}>
              <TextField
                name="banco"
                label="Banco *"
                defaultValue={editingRecord?.banco || ''}
                required
                fullWidth
              />
              <TextField
                name="tabela"
                label="Tabela *"
                defaultValue={editingRecord?.tabela || ''}
                required
                fullWidth
              />
              <TextField
                name="coluna"
                label="Coluna *"
                defaultValue={editingRecord?.coluna || ''}
                required
                fullWidth
              />
              <TextField
                name="label_formulario"
                label="Label Formulário"
                defaultValue={editingRecord?.label_formulario || ''}
                fullWidth
              />
              <TextField
                name="label_grid"
                label="Label Grid"
                defaultValue={editingRecord?.label_grid || ''}
                fullWidth
              />
              <TextField
                name="tipo_dado"
                label="Tipo de Dado"
                defaultValue={editingRecord?.tipo_dado || ''}
                fullWidth
              />
              <TextField
                name="tamanho_maximo"
                label="Tamanho Máximo"
                type="number"
                defaultValue={editingRecord?.tamanho_maximo || ''}
                fullWidth
              />
              <TextField
                name="valores_possiveis"
                label="Valores Possíveis"
                defaultValue={editingRecord?.valores_possiveis || ''}
                fullWidth
                helperText="Ex: 'A,I' ou 'true,false'"
              />
              <TextField
                name="descritivo_help"
                label="Descrição Help"
                multiline
                rows={3}
                defaultValue={editingRecord?.descritivo_help || ''}
                fullWidth
              />
              <TextField
                name="prompt_ia"
                label="Prompt IA"
                multiline
                rows={3}
                defaultValue={editingRecord?.prompt_ia || ''}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formObrigatorio}
                    onChange={(e) => setFormObrigatorio(e.target.checked)}
                  />
                }
                label="Obrigatório"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formAtivo}
                    onChange={(e) => setFormAtivo(e.target.checked)}
                  />
                }
                label="Ativo"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingRecord ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DicionarioDadosPage
